// src/components/marketing/ComposerV2.tsx
// Rich inline composer for FB + IG. Replaces the bare PublishPanel that
// only accepted text. Supports:
//   - Multi-media upload with drag/drop, reorder, remove, alt text
//   - Platform multi-select (publishes to both as independent posts)
//   - Post type: feed | story | reel (reel requires video)
//   - Hashtag chip input
//   - Schedule datetime (empty = borrador / publish-now)
//   - Preview pane per platform
//
// Backend contract reused from api_social_publish: createPost + attachMedia
// per upload + optional publishNow. Cross-posting creates N ScheduledPost
// rows (one per platform) so each can track its own meta_post_id + status.

import { useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, Calendar, Check, Facebook, GripVertical, Hash, Image as ImageIcon,
  Instagram, Loader2, Send, Trash2, Upload, X, Video as VideoIcon, Clock,
} from 'lucide-react';

import {
  ApiError, attachMedia, createPost, publishNow, uploadMedia,
  type MediaKind, type MediaUploadOut, type Platform, type PostType,
  type ScheduledPostIn,
} from '@/lib/api_social_publish';
import { useActiveBusiness } from '@/context/BusinessContext';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string }) => void;

interface MediaSlot {
  id: string;                    // local uid
  file?: File;                   // kept until upload finishes
  uploaded?: MediaUploadOut;
  uploading?: boolean;
  error?: string;
  altText?: string;
}

interface Props {
  addNotification?: Notifier;
  onPublished?: () => void;
}

const MAX_MEDIA = 10;
const DEFAULT_POST_TYPES: Array<{ value: PostType; label: string; hint: string }> = [
  { value: 'feed', label: 'Feed',   hint: 'Post normal al muro/timeline.' },
  { value: 'story', label: 'Story', hint: 'Formato vertical 9:16, 24h.' },
  { value: 'reel', label: 'Reel',   hint: 'Video vertical ≥ 3s. Requiere video.' },
];

export function ComposerV2({ addNotification, onPublished }: Props) {
  const { activeId: businessId } = useActiveBusiness();

  const [platforms, setPlatforms] = useState<Set<Platform>>(new Set(['facebook']));
  const [postType, setPostType] = useState<PostType>('feed');
  const [caption, setCaption] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [scheduled, setScheduled] = useState(''); // empty = now / draft
  const [publishMode, setPublishMode] = useState<'draft' | 'now' | 'schedule'>('now');
  const [media, setMedia] = useState<MediaSlot[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<Array<{ platform: Platform; ok: boolean; detail?: string }>>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const hasVideo = media.some((m) => m.uploaded?.kind === 'video');
  const hasImage = media.some((m) => m.uploaded?.kind === 'image');
  const totalChars = caption.length + tags.reduce((n, t) => n + t.length + 2, 0);

  // ── Media ─────────────────────────────────────────────────

  async function addFiles(files: FileList | File[]) {
    const arr = Array.from(files).slice(0, MAX_MEDIA - media.length);
    if (!arr.length) return;
    const slots: MediaSlot[] = arr.map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      uploading: true,
    }));
    setMedia((prev) => [...prev, ...slots]);
    for (const s of slots) {
      try {
        const out = await uploadMedia(s.file!);
        setMedia((prev) => prev.map((m) => m.id === s.id
          ? { ...m, uploaded: out, uploading: false, file: undefined }
          : m,
        ));
      } catch (e: any) {
        const msg = e instanceof ApiError ? e.message : (e?.message || 'Error subiendo archivo');
        setMedia((prev) => prev.map((m) => m.id === s.id
          ? { ...m, error: msg, uploading: false }
          : m,
        ));
      }
    }
  }

  function removeMedia(id: string) {
    setMedia((prev) => prev.filter((m) => m.id !== id));
  }

  function moveMedia(id: string, dir: -1 | 1) {
    setMedia((prev) => {
      const idx = prev.findIndex((m) => m.id === id);
      if (idx < 0) return prev;
      const j = idx + dir;
      if (j < 0 || j >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[j]] = [copy[j], copy[idx]];
      return copy;
    });
  }

  function setAlt(id: string, alt: string) {
    setMedia((prev) => prev.map((m) => m.id === id ? { ...m, altText: alt } : m));
  }

  // ── Hashtags ──────────────────────────────────────────────

  function commitTag(raw: string) {
    const clean = raw.trim().replace(/^#+/, '').replace(/\s+/g, '');
    if (!clean) return;
    if (tags.includes(clean)) return;
    setTags((prev) => [...prev, clean]);
    setTagInput('');
  }

  function onTagKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      commitTag(tagInput);
    } else if (e.key === 'Backspace' && !tagInput && tags.length) {
      setTags((prev) => prev.slice(0, -1));
    }
  }

  // ── Platforms ─────────────────────────────────────────────

  function togglePlatform(p: Platform) {
    setPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      if (next.size === 0) next.add(p); // always keep at least one
      return next;
    });
  }

  // ── Submit ────────────────────────────────────────────────

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (platforms.size === 0) return false;
    if (!caption.trim() && !media.some((m) => m.uploaded)) return false;
    if (platforms.has('instagram') && !media.some((m) => m.uploaded)) return false;
    if (postType === 'reel' && !hasVideo) return false;
    if (media.some((m) => m.uploading)) return false;
    return true;
  }, [platforms, caption, media, postType, submitting, hasVideo]);

  async function onSubmit(modeOverride?: 'draft' | 'now' | 'schedule') {
    const effectiveMode = modeOverride ?? publishMode;
    // Si schedule pero sin datetime, expander el picker (no ejecutar todavía).
    if (effectiveMode === 'schedule' && !scheduled) {
      setPublishMode('schedule');
      setError('Elige fecha y hora antes de programar.');
      return;
    }
    if (effectiveMode !== publishMode) setPublishMode(effectiveMode);
    if (!canSubmit) return;
    if (!businessId) {
      setError('Selecciona un negocio primero (Empresa → Negocios).');
      return;
    }
    setSubmitting(true);
    setError(null);
    setLastResult([]);

    const uploadedMedia = media.filter((m) => m.uploaded);
    const scheduledIso = effectiveMode === 'schedule' && scheduled
      ? new Date(scheduled).toISOString()
      : null;

    const results: Array<{ platform: Platform; ok: boolean; detail?: string }> = [];

    for (const platform of platforms) {
      try {
        const payload: ScheduledPostIn = {
          business_id: businessId,
          platform,
          post_type: postType,
          caption: caption.trim(),
          hashtags: tags,
          scheduled_at: scheduledIso,
        };
        const post = await createPost(payload);
        for (let i = 0; i < uploadedMedia.length; i++) {
          const m = uploadedMedia[i];
          await attachMedia(post.id, {
            kind: m.uploaded!.kind as MediaKind,
            media_url: m.uploaded!.media_url,
            alt_text: m.altText || '',
            sort_order: i,
          });
        }
        if (effectiveMode === 'now') {
          await publishNow(post.id, platform);
          results.push({ platform, ok: true, detail: 'Publicando…' });
        } else if (effectiveMode === 'schedule') {
          results.push({ platform, ok: true, detail: `Programado para ${new Date(scheduledIso!).toLocaleString()}` });
        } else {
          results.push({ platform, ok: true, detail: 'Borrador guardado' });
        }
      } catch (e: any) {
        const msg = e instanceof ApiError ? e.message : (e?.message || 'Error');
        results.push({ platform, ok: false, detail: msg });
      }
    }

    setLastResult(results);
    setSubmitting(false);
    const allOk = results.every((r) => r.ok);
    if (allOk) {
      addNotification?.({
        type: 'success',
        title: effectiveMode === 'now' ? 'Publicando' : effectiveMode === 'schedule' ? 'Programado' : 'Borrador guardado',
        message: results.map((r) => `${r.platform}: ${r.detail}`).join(' · '),
      });
      // Reset composer
      setCaption('');
      setTags([]);
      setMedia([]);
      onPublished?.();
    } else {
      addNotification?.({
        type: 'error',
        title: 'Algunas plataformas fallaron',
        message: results.filter((r) => !r.ok).map((r) => `${r.platform}: ${r.detail}`).join(' · '),
      });
    }
  }

  // ── Render ────────────────────────────────────────────────

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-6 max-w-[1200px]">
      {/* ── Left: controls ── */}
      <div className="space-y-4">
        {/* Platforms */}
        <div className="o-card p-3 space-y-2">
          <div className="text-xs uppercase tracking-wider text-white/50">Plataformas</div>
          <div className="flex gap-2">
            <PlatformChip
              active={platforms.has('facebook')} icon={<Facebook size={14} />} label="Facebook"
              onClick={() => togglePlatform('facebook')}
            />
            <PlatformChip
              active={platforms.has('instagram')} icon={<Instagram size={14} />} label="Instagram"
              onClick={() => togglePlatform('instagram')}
            />
          </div>
          <div className="text-xs text-white/40">
            Se crea un post independiente por plataforma (cada uno con su propio estado y analytics).
          </div>
        </div>

        {/* Post type */}
        <div className="o-card p-3 space-y-2">
          <div className="text-xs uppercase tracking-wider text-white/50">Tipo de post</div>
          <div className="flex gap-2 flex-wrap">
            {DEFAULT_POST_TYPES.map((t) => (
              <button
                key={t.value}
                onClick={() => setPostType(t.value)}
                className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
                  postType === t.value
                    ? 'bg-[var(--acc1,#00e5ff)]/20 border-[var(--acc1,#00e5ff)]/40 text-[var(--acc1,#00e5ff)]'
                    : 'border-white/10 hover:border-white/20 text-white/70'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="text-xs text-white/40">
            {DEFAULT_POST_TYPES.find((t) => t.value === postType)?.hint}
          </div>
          {postType === 'reel' && !hasVideo && (
            <div className="flex items-center gap-1 text-amber-300/90 text-xs">
              <AlertTriangle size={11} /> Reels requieren un video vertical.
            </div>
          )}
        </div>

        {/* Caption */}
        <div className="o-card p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider text-white/50">Caption</div>
            <div className="text-xs text-white/40">{caption.length.toLocaleString()} / 2200</div>
          </div>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={5}
            placeholder="¿Qué vas a publicar?"
            className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none text-sm focus:border-[var(--acc1,#00e5ff)]/50 resize-y"
          />
        </div>

        {/* Hashtags */}
        <div className="o-card p-3 space-y-2">
          <div className="text-xs uppercase tracking-wider text-white/50 flex items-center gap-1">
            <Hash size={12} /> Hashtags
          </div>
          <div className="flex flex-wrap gap-1.5 p-2 rounded-md bg-white/5 border border-white/10 focus-within:border-[var(--acc1,#00e5ff)]/50">
            {tags.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[var(--acc1,#00e5ff)]/15 text-[var(--acc1,#00e5ff)] text-xs">
                #{t}
                <button onClick={() => setTags((prev) => prev.filter((x) => x !== t))} className="hover:text-white">
                  <X size={10} />
                </button>
              </span>
            ))}
            <input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={onTagKeyDown}
              onBlur={() => commitTag(tagInput)}
              placeholder={tags.length ? '' : 'marketing seo travel'}
              className="flex-1 min-w-[140px] bg-transparent text-sm outline-none"
            />
          </div>
          <div className="text-xs text-white/40">
            Enter, espacio o coma para agregar. {totalChars} caracteres totales.
          </div>
        </div>

        {/* Media */}
        <div className="o-card p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider text-white/50">
              Media ({media.length}/{MAX_MEDIA})
            </div>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={media.length >= MAX_MEDIA}
              className="px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 text-xs flex items-center gap-1 disabled:opacity-40"
            >
              <Upload size={12} /> Agregar
            </button>
            <input
              ref={fileRef} type="file" multiple hidden
              accept="image/*,video/*"
              onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }}
            />
          </div>
          <DropZone onDrop={addFiles} empty={media.length === 0} />
          {media.length > 0 && (
            <div className="space-y-2">
              {media.map((m, i) => (
                <MediaRow
                  key={m.id} slot={m} idx={i} total={media.length}
                  onRemove={() => removeMedia(m.id)}
                  onMove={(d) => moveMedia(m.id, d)}
                  onAlt={(a) => setAlt(m.id, a)}
                />
              ))}
            </div>
          )}
          {platforms.has('instagram') && media.length === 0 && (
            <div className="flex items-center gap-1 text-amber-300/90 text-xs">
              <AlertTriangle size={11} /> Instagram requiere al menos una imagen o video.
            </div>
          )}
        </div>

        {/* Action buttons (unified — antes habia 3 chips selectores + 1 boton
            footer; era confuso. Ahora cada chip ES el boton de accion). */}
        <div className="o-card p-3 space-y-3">
          <div className="text-xs uppercase tracking-wider text-white/50">Acción</div>
          {publishMode === 'schedule' && (
            <input
              type="datetime-local"
              value={scheduled}
              onChange={(e) => setScheduled(e.target.value)}
              className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none text-sm focus:border-[var(--acc1,#00e5ff)]/50"
            />
          )}
          {error && (
            <div className="p-3 rounded-lg border border-red-500/40 bg-red-500/10 text-sm text-red-200 flex items-start gap-2">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          )}
          {lastResult.length > 0 && (
            <div className="space-y-1">
              {lastResult.map((r) => (
                <div key={r.platform} className={`flex items-center gap-2 text-xs p-2 rounded-md border ${
                  r.ok ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
                       : 'border-red-500/40 bg-red-500/10 text-red-200'
                }`}>
                  {r.ok ? <Check size={12} /> : <AlertTriangle size={12} />}
                  <span className="font-medium capitalize">{r.platform}:</span> {r.detail}
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            <ActionButton
              primary
              disabled={!canSubmit || submitting}
              loading={submitting && publishMode === 'now'}
              icon={<Send size={14} />}
              label="Publicar ahora"
              onClick={() => onSubmit('now')}
            />
            <ActionButton
              disabled={submitting || !canSubmit}
              loading={submitting && publishMode === 'schedule'}
              icon={<Calendar size={14} />}
              label="Programar"
              onClick={() => onSubmit('schedule')}
            />
            <ActionButton
              disabled={submitting || !canSubmit}
              loading={submitting && publishMode === 'draft'}
              icon={<Clock size={14} />}
              label="Guardar borrador"
              onClick={() => onSubmit('draft')}
            />
          </div>
        </div>
      </div>

      {/* ── Right: preview ── */}
      <div className="space-y-4">
        <div className="text-xs uppercase tracking-wider text-white/50">Preview</div>
        {platforms.has('facebook') && (
          <FacebookPreview caption={caption} tags={tags} media={media} postType={postType} />
        )}
        {platforms.has('instagram') && (
          <InstagramPreview caption={caption} tags={tags} media={media} postType={postType} />
        )}
        {platforms.size === 0 && (
          <div className="text-xs text-white/40 italic">Activa una plataforma para ver el preview.</div>
        )}
      </div>
    </div>
  );
}

export default ComposerV2;

// ═══════════════════════════════════════════════════════════════
// Bits
// ═══════════════════════════════════════════════════════════════

function PlatformChip({ active, icon, label, onClick }: {
  active: boolean; icon: React.ReactNode; label: string; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-sm border flex items-center gap-2 transition-colors ${
        active
          ? 'bg-[var(--acc1,#00e5ff)]/20 border-[var(--acc1,#00e5ff)]/40 text-[var(--acc1,#00e5ff)]'
          : 'border-white/10 hover:border-white/20 text-white/70'
      }`}
    >
      {icon} {label}
    </button>
  );
}

function ActionButton({ primary, disabled, loading, icon, label, onClick }: {
  primary?: boolean; disabled?: boolean; loading?: boolean;
  icon: React.ReactNode; label: string; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`px-4 py-2 rounded-md text-sm border flex items-center gap-2 font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
        primary
          ? 'bg-[var(--acc1,#00e5ff)]/25 hover:bg-[var(--acc1,#00e5ff)]/40 border-[var(--acc1,#00e5ff)]/50 text-[var(--acc1,#00e5ff)]'
          : 'border-white/15 hover:border-white/30 hover:bg-white/5 text-white/80'
      }`}
    >
      {loading ? <Loader2 size={14} className="animate-spin" /> : icon}
      {label}
    </button>
  );
}

function DropZone({ onDrop, empty }: { onDrop: (f: FileList | File[]) => void; empty: boolean }) {
  const [dragging, setDragging] = useState(false);
  return (
    <div
      onDragEnter={() => setDragging(true)}
      onDragLeave={() => setDragging(false)}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDrop={(e) => {
        e.preventDefault(); setDragging(false);
        if (e.dataTransfer.files) onDrop(e.dataTransfer.files);
      }}
      className={`rounded-md border-2 border-dashed p-4 text-center text-xs transition-colors ${
        dragging ? 'border-[var(--acc1,#00e5ff)]/60 bg-[var(--acc1,#00e5ff)]/5'
                 : 'border-white/15 bg-white/5'
      }`}
    >
      {empty
        ? 'Arrastra imágenes o videos aquí — max 10 archivos, 100 MB cada uno.'
        : 'Suelta más archivos para agregarlos al carrusel.'}
    </div>
  );
}

function MediaRow({ slot, idx, total, onRemove, onMove, onAlt }: {
  slot: MediaSlot; idx: number; total: number;
  onRemove: () => void; onMove: (d: -1 | 1) => void; onAlt: (a: string) => void;
}) {
  return (
    <div className="flex items-start gap-2 p-2 rounded-md border border-white/10 bg-white/5">
      <div className="flex flex-col gap-0.5">
        <button onClick={() => onMove(-1)} disabled={idx === 0}
          className="text-white/40 hover:text-white disabled:opacity-20">
          <GripVertical size={12} />
        </button>
      </div>
      <div className="shrink-0">
        {slot.uploading ? (
          <div className="w-14 h-14 rounded-md bg-black/40 flex items-center justify-center">
            <Loader2 size={16} className="animate-spin opacity-60" />
          </div>
        ) : slot.error ? (
          <div className="w-14 h-14 rounded-md bg-red-500/20 flex items-center justify-center">
            <AlertTriangle size={14} className="text-red-300" />
          </div>
        ) : slot.uploaded?.kind === 'image' ? (
          <img src={slot.uploaded.media_url} alt="preview"
            className="w-14 h-14 object-cover rounded-md" />
        ) : (
          <div className="w-14 h-14 rounded-md bg-black/50 flex items-center justify-center">
            <VideoIcon size={16} className="opacity-70" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-white/40">{idx + 1} / {total}</span>
          {slot.uploaded && (
            <span className="text-white/60 truncate">
              {slot.uploaded.original_filename} · {(slot.uploaded.size_bytes / 1024).toFixed(0)} KB
            </span>
          )}
          {slot.error && <span className="text-red-300">{slot.error}</span>}
        </div>
        {slot.uploaded && (
          <input
            type="text"
            placeholder="Alt text (accesibilidad, opcional)"
            value={slot.altText || ''}
            onChange={(e) => onAlt(e.target.value)}
            className="w-full px-2 py-1 rounded text-xs bg-white/5 border border-white/10 outline-none"
          />
        )}
      </div>
      <div className="flex flex-col gap-1">
        <button onClick={() => onMove(1)} disabled={idx === total - 1}
          className="text-white/40 hover:text-white disabled:opacity-20" title="Bajar">
          ↓
        </button>
        <button onClick={onRemove} className="text-red-300/80 hover:text-red-300" title="Quitar">
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

// ─── Previews ───

function FacebookPreview({ caption, tags, media, postType }: {
  caption: string; tags: string[]; media: MediaSlot[]; postType: PostType;
}) {
  const imgs = media.filter((m) => m.uploaded);
  const captionFull = `${caption}${tags.length ? '\n\n' + tags.map((t) => `#${t}`).join(' ') : ''}`;
  return (
    <div className="rounded-xl border border-white/10 bg-[#0a0b11] overflow-hidden">
      <div className="px-4 py-2.5 flex items-center gap-2 border-b border-white/5">
        <Facebook size={14} className="text-[#1877f2]" />
        <span className="text-xs text-white/60">Facebook · {postType}</span>
      </div>
      <div className="px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-white/10" />
          <div className="flex-1">
            <div className="text-xs font-semibold">Tu Página</div>
            <div className="text-[10px] text-white/40">Hace un momento · 🌐</div>
          </div>
        </div>
        <div className="text-sm whitespace-pre-wrap">{captionFull || <span className="text-white/30 italic">Sin caption…</span>}</div>
      </div>
      {imgs.length > 0 && (
        <div className={`grid ${imgs.length === 1 ? '' : 'grid-cols-2'} gap-0.5`}>
          {imgs.slice(0, 4).map((m) => (
            <div key={m.id} className="aspect-video bg-black/40 overflow-hidden">
              {m.uploaded!.kind === 'image'
                ? <img src={m.uploaded!.media_url} className="w-full h-full object-cover" alt="" />
                : <div className="w-full h-full flex items-center justify-center"><VideoIcon className="opacity-70" /></div>}
            </div>
          ))}
        </div>
      )}
      <div className="px-4 py-2 flex justify-between text-[11px] text-white/40 border-t border-white/5">
        <span>👍 Me gusta</span><span>💬 Comentar</span><span>🔗 Compartir</span>
      </div>
    </div>
  );
}

function InstagramPreview({ caption, tags, media, postType }: {
  caption: string; tags: string[]; media: MediaSlot[]; postType: PostType;
}) {
  const imgs = media.filter((m) => m.uploaded);
  const captionFull = `${caption}${tags.length ? ' ' + tags.map((t) => `#${t}`).join(' ') : ''}`;
  const ratioClass = postType === 'story' || postType === 'reel' ? 'aspect-[9/16]' : 'aspect-square';
  return (
    <div className="rounded-xl border border-white/10 bg-[#0a0b11] overflow-hidden">
      <div className="px-4 py-2.5 flex items-center gap-2 border-b border-white/5">
        <Instagram size={14} className="text-[#E4405F]" />
        <span className="text-xs text-white/60">Instagram · {postType}</span>
      </div>
      <div className="px-4 py-2 flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737]" />
        <div className="text-xs font-semibold">tu_cuenta</div>
      </div>
      <div className={`${ratioClass} bg-black/40 overflow-hidden relative`}>
        {imgs[0]?.uploaded?.kind === 'image' ? (
          <img src={imgs[0].uploaded.media_url} className="w-full h-full object-cover" alt="" />
        ) : imgs[0]?.uploaded?.kind === 'video' ? (
          <video src={imgs[0].uploaded.media_url} className="w-full h-full object-cover" controls={false} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">
            <ImageIcon size={18} className="mr-1" /> Agrega media
          </div>
        )}
        {imgs.length > 1 && (
          <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded-md bg-black/60 text-[10px]">
            1/{imgs.length}
          </div>
        )}
      </div>
      {postType !== 'story' && (
        <div className="px-4 py-3 space-y-1">
          <div className="flex gap-3 text-white/60 text-xs">♥ 💬 ✈ · 🔖</div>
          <div className="text-xs whitespace-pre-wrap">
            <span className="font-semibold mr-1">tu_cuenta</span>
            {captionFull || <span className="text-white/30 italic">Sin caption…</span>}
          </div>
        </div>
      )}
    </div>
  );
}
